import { useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, EyeVisible } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsContractTypesEnum,
  AtsDeleteOfferLetterTemplateDocument,
  AtsJobPostingScheduleTypesEnum,
  AtsOfferLetterTemplatesAuthorsFilterDocument,
  AtsOfferLetterTemplatesContractTypesDocument,
  AtsOfferLetterTemplatesLegalEntitiesFilterDocument,
  AtsOfferLetterTemplatesTagsFilterDocument,
  AtsSettingsOfferLetterTemplatesDocument,
  AtsSettingsOfferLetterTemplatesItemFragment,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { CreateOfferLetterTemplateModal } from 'modules/ats/pages/settings/TemplatesPage/sections/OfferLetterTemplates/CreateTemplate'
import { OfferLetterTemplateSuccessPreviewModal } from 'modules/ats/pages/settings/TemplatesPage/sections/OfferLetterTemplates/SuccessPreviewModal'

interface OfferLetterTemplatesTableFilters {
  authorIds?: number[]
  contractTypes?: AtsContractTypesEnum[]
  legalEntityIds?: number[]
  workplaceTypes?: AtsWorkplaceTypesEnum[]
  scheduleTypes?: AtsJobPostingScheduleTypesEnum[]
  tags?: string[]
}

const DEFAULT_FILTERS: OfferLetterTemplatesTableFilters = {}

const toContractTypes = (
  value: OfferLetterTemplatesTableFilters['contractTypes']
): AtsContractTypesEnum[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is AtsContractTypesEnum => typeof v === 'string')
}

const toStringArray = (value: OfferLetterTemplatesTableFilters['tags']): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is string => typeof v === 'string')
}

const toNumberArray = (
  value:
    | OfferLetterTemplatesTableFilters['authorIds']
    | OfferLetterTemplatesTableFilters['legalEntityIds']
): number[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is number => typeof v === 'number')
}

export const OfferLetterTemplatesTable = () => {
  const client = useApolloClient()
  const { openDialog } = useConfirmationDialog()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [successPreviewTemplateId, setSuccessPreviewTemplateId] = useState<number | undefined>(
    undefined
  )
  const [savedFilters, setSavedFilters] = useLocalStorage<
    OfferLetterTemplatesTableFilters | undefined
  >(LOCAL_STORAGE_KEYS.atsOfferLetterTemplatesTableFilters)

  const { data: contractTypesData } = useQuery(AtsOfferLetterTemplatesContractTypesDocument)

  const contractTypeLabelMap = useMemo(() => {
    const map = new Map<AtsContractTypesEnum, string>()
    contractTypesData?.ats.contractTypesConnection.nodes.forEach((contractType) => {
      map.set(contractType.id, contractType.label)
    })
    return map
  }, [contractTypesData])

  const [deleteAtsOfferLetterTemplate] = useMutation(AtsDeleteOfferLetterTemplateDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsOfferLetterTemplate,
    refetchQueries: [
      { query: AtsSettingsOfferLetterTemplatesDocument },
      { query: AtsOfferLetterTemplatesTagsFilterDocument },
      { query: AtsOfferLetterTemplatesAuthorsFilterDocument },
    ],
    awaitRefetchQueries: true,
  })

  const removeOfferLetterTemplate = async (id: number) => {
    const result = await deleteAtsOfferLetterTemplate({
      variables: { id },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.removed_failed'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: AtsSettingsOfferLetterTemplatesDocument,
    mapData: (data) =>
      data.ats.offerLetterTemplatesConnection.nodes.map((node) => ({
        ...node,
        team_id: node.team?.id ?? null,
        legal_entity_id: node.legalEntity?.id ?? null,
      })),
    mapPagination: (data) => ({
      total: data.ats.offerLetterTemplatesConnection.totalCount ?? 0,
    }),
    currentFilters: savedFilters ?? DEFAULT_FILTERS,
    mapVariables: ({ search, filters, sortings }) => {
      const authorIds = toNumberArray(filters.authorIds)
      const contractTypes = toContractTypes(filters.contractTypes)
      const legalEntityIds = toNumberArray(filters.legalEntityIds)
      const tags = toStringArray(filters.tags)

      setSavedFilters({
        authorIds,
        contractTypes,
        legalEntityIds,
        tags,
      })

      return {
        search,
        authorIds,
        contractTypes,
        legalEntityIds,
        tags,
        sort: sortings?.[0],
      }
    },
    perPage: 20,
    search: { enabled: true },
    sortings: {
      name: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.name'),
      },
      legal_entity_id: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.legal_entity'),
      },
    },
    filters: {
      contractTypes: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.agreement_type'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesContractTypesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.contractTypesConnection.nodes.map((contractType) => ({
                label: contractType.label || '',
                value: contractType.id,
              })) || []
            )
          },
        },
      },
      legalEntityIds: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.legal_entity'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesLegalEntitiesFilterDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.companies.legalEntities.map(
                (legalEntity: { id: number; legalName: string | null }) => ({
                  label: legalEntity.legalName || '',
                  value: legalEntity.id,
                })
              ) || []
            )
          },
        },
      },
      authorIds: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.created_by'),
        type: 'in',
        options: {
          cache: false,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesAuthorsFilterDocument,
              fetchPolicy: 'network-only',
            })

            const authorsMap = new Map<number, { firstName: string; lastName: string }>()
            result.data?.ats.offerLetterTemplates.forEach(
              (template: {
                author?: { id: number; firstName: string; lastName: string } | null
              }) => {
                if (template.author?.id) {
                  authorsMap.set(template.author.id, {
                    firstName: template.author.firstName || '',
                    lastName: template.author.lastName || '',
                  })
                }
              }
            )

            return Array.from(authorsMap.entries())
              .sort(([, a], [, b]) => {
                const nameA = `${a.firstName} ${a.lastName}`.trim()
                const nameB = `${b.firstName} ${b.lastName}`.trim()
                return nameA.localeCompare(nameB)
              })
              .map(([id, author]) => ({
                label: `${author.firstName} ${author.lastName}`.trim() || String(id),
                value: id,
              }))
          },
        },
      },
      tags: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.tags'),
        type: 'in',
        options: {
          cache: false,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesTagsFilterDocument,
              fetchPolicy: 'network-only',
            })

            const allTags = new Set<string>()
            result.data?.ats.offerLetterTemplates.forEach(
              (template: { tags?: (string | null)[] | null }) => {
                template.tags?.forEach((tag: string | null | undefined) => {
                  if (tag) {
                    allTags.add(tag)
                  }
                })
              }
            )

            return Array.from(allTags)
              .sort()
              .map((tag) => ({
                label: tag,
                value: tag,
              }))
          },
        },
      },
    },
    primaryActions: () => {
      return [
        {
          label: i18n.t('ats.settings.templates.offer_letter_templates.create_template'),
          icon: Add,
          onClick: () => setShowCreateModal(true),
        },
      ]
    },
    itemActions: (offerLetterTemplate) => [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.view_template'),
        icon: EyeVisible,
        onClick: () => setSuccessPreviewTemplateId(offerLetterTemplate.id),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => {
          openDialog({
            title: i18n.t('ats.settings.templates.offer_letter_templates.delete_modal.title'),
            description: i18n.t(
              'ats.settings.templates.offer_letter_templates.delete_modal.description'
            ),
            cancelLabel: i18n.t('common.cancel'),
            confirmLabel: i18n.t('common.delete'),
            onConfirm: () => removeOfferLetterTemplate(offerLetterTemplate.id),
          })
        },
      },
    ],
  })

  const tableColumns = useMemo(
    () => [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.name'),
        sorting: 'name' as const,
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.name,
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.created_by'),
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'person' as const,
          value: {
            firstName: template.author?.firstName ?? '',
            lastName: template.author?.lastName ?? '',
            src: buildUrl(template.author?.avatar?.url ?? ''),
          },
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.legal_entity'),
        sorting: 'legal_entity_id' as const,
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.legalEntity?.legalName || '',
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.agreement_type'),
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const contractTypeLabels =
            template.contractTypes
              ?.map((contractType) => contractTypeLabelMap.get(contractType))
              .filter(Boolean) ?? []
          const joinedLabels = contractTypeLabels.join(', ')

          return {
            type: 'text' as const,
            value: joinedLabels,
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.tags'),
        render: ({ tags }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const items = tags?.filter((tag) => tag !== null && tag !== undefined) || []
          if (items.length > 0) {
            return {
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: items.map((tag) => ({
                  text: tag,
                })),
              },
            }
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.detected_variables'),
        render: ({
          systemVariables,
          customVariables,
        }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const systemCount = systemVariables?.length ?? 0
          const customCount = customVariables?.length ?? 0
          const totalCount = systemCount + customCount

          return {
            type: 'alertTag' as const,
            value: {
              label: String(totalCount),
              level: totalCount === 0 ? ('warning' as const) : ('info' as const),
            },
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.created_at'),
        render: ({ createdAt }: AtsSettingsOfferLetterTemplatesItemFragment) =>
          convertDate(createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
    ],
    [contractTypeLabelMap]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns: tableColumns,
        },
      },
    ],
    [tableColumns]
  )

  return (
    <>
      <OneDataCollection source={source} visualizations={visualizations} />
      {showCreateModal && (
        <CreateOfferLetterTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(id) => {
            setShowCreateModal(false)
            setSuccessPreviewTemplateId(id)
          }}
        />
      )}

      {!!successPreviewTemplateId && (
        <OfferLetterTemplateSuccessPreviewModal
          onClose={() => setSuccessPreviewTemplateId(undefined)}
          id={successPreviewTemplateId}
        />
      )}
    </>
  )
}
